<template>
  <div class="container">

    <div class="row">
      <div class="col-sm-6">
        <date-range-picker @daterange-change="dateRangeChange"></date-range-picker>
      </div>
      <div class="col-sm-6">
        <h5>{{ dateRangeFormat(dateStart) }} - {{ dateRangeFormat(dateEnd) }}</h5>
      </div>
    </div>

    <bootstrap-panel title="Appliance Locations">
      <google-map :coordinates="trackCoordinates" height="400px"></google-map>
    </bootstrap-panel>

    <div class="row">
      <div class="col-sm-6 col-md-6">
        <high-chart :options="appliancesChartOptions" :series="appliancesChartSeries"></high-chart>
      </div>
      <div class="col-sm-6 col-md-6">
        <high-chart :options="versionsChartOptions" :series="versionsChartSeries"></high-chart>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-3 col-md-3">
        <high-chart :options="requestsChartOptions" :series="requestsChartSeries"></high-chart>
      </div>
      <div class="col-sm-3 col-md-3">
        <high-chart :options="malwareChartOptions" :series="malwareChartSeries"></high-chart>
      </div>
      <div class="col-sm-3 col-md-3">
        <high-chart :options="bytesChartOptions" :series="bytesChartSeries"></high-chart>
      </div>
      <div class="col-sm-3 col-md-3">
        <high-chart :options="connectionsChartOptions" :series="connectionsChartSeries"></high-chart>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-3 col-md-3">
        <high-chart :options="aiMalwareChartOptions" :series="aiMalwareChartSeries"></high-chart>
      </div>
      <div class="col-sm-3 col-md-3">
        <high-chart :options="aiGraywareChartOptions" :series="aiGraywareChartSeries"></high-chart>
      </div>
      <div class="col-sm-3 col-md-3">
        <high-chart :options="sandboxMalwareChartOptions" :series="sandboxMalwareChartSeries"></high-chart>
      </div>
      <div class="col-sm-3 col-md-3">
        <high-chart :options="sandboxCleanChartOptions" :series="sandboxCleanChartSeries"></high-chart>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-3 col-md-3">
        <high-chart :options="urlsChartOptions" :series="urlsChartSeries"></high-chart>
      </div>
      <div class="col-sm-3 col-md-3">
        <high-chart :options="spamChartOptions" :series="spamChartSeries"></high-chart>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>

<script>
import _ from 'underscore'
import moment from 'moment'
import * as mutationTypes from './store/mutation-types'
import BootstrapPanel from './components/BootstrapPanel'
import DateRangePicker from './components/DateRangePicker'
import HighChart from './components/HighChart'
import GoogleMap from './components/GoogleMap'
import C from './constants'
import statsLib from './lib/stats'

export default {
  components: {
    BootstrapPanel,
    DateRangePicker,
    HighChart,
    GoogleMap
  },

  data () {
    return {
      // api data
      totalAppliances: {},
      softwareVersion: {},
      proxyProtocolStats: {},

      reducedVersions: {},
      reducedProxyProtocolStats: {},

      // map coordinates
      trackCoordinates: [],

      // chart series and options
      malwareChartSeries: [],
      malwareChartOptions: { chart: {} },

      requestsChartSeries: [],
      requestsChartOptions: { chart: {} },

      bytesChartSeries: [],
      bytesChartOptions: { chart: {} },

      connectionsChartSeries: [],
      connectionsChartOptions: { chart: {} },

      urlsChartSeries: [],
      urlsChartOptions: { chart: {} },

      aiMalwareChartSeries: [],
      aiMalwareChartOptions: { chart: {} },

      aiGraywareChartSeries: [],
      aiGraywareChartOptions: { chart: {} },

      sandboxMalwareChartSeries: [],
      sandboxMalwareChartOptions: { chart: {} },

      sandboxCleanChartSeries: [],
      sandboxCleanChartOptions: { chart: {} },

      spamChartSeries: [],
      spamChartOptions: { chart: {} },

      appliancesChartSeries: [{
        name: 'Appliances',
        data: []
      }],
      appliancesChartOptions: {
        chart: {
          type: 'column'
        },
        title: {
          text: 'Tracked Appliances'
        },
        xAxis: {
          categories: [],
          crosshair: true
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Count'
          }
        },
        tooltip: {
          headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
          pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
          '<td style="padding:0"><b>{point.y}</b></td></tr>',
          footerFormat: '</table>',
          shared: true,
          useHTML: true
        },
        plotOptions: {
          column: {
            pointPadding: 0.2,
            borderWidth: 0
          }
        }
      },

      versionsChartSeries: [{
        name: 'Versions',
        data: []
      }],
      versionsChartOptions: {
        chart: {
          type: 'column'
        },
        title: {
          text: 'Software Versions'
        },
        xAxis: {
          categories: [],
          crosshair: true
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Count'
          }
        },
        tooltip: {
          headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
          pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
          '<td style="padding:0"><b>{point.y}</b></td></tr>',
          footerFormat: '</table>',
          shared: true,
          useHTML: true
        },
        plotOptions: {
          column: {
            pointPadding: 0.2,
            borderWidth: 0
          }
        }
      }
    }
  },

  mounted () {
    this.malwareChartOptions = this.chartOptions('column', 'AV Malware', 'count')
    this.bytesChartOptions = this.chartOptions('column', 'Bytes', 'count')
    this.connectionsChartOptions = this.chartOptions('column', 'Connections', 'count')
    this.requestsChartOptions = this.chartOptions('column', 'Requests', 'count')

    this.aiMalwareChartOptions = this.chartOptions('column', 'AI Malware', 'count')
    this.aiGraywareChartOptions = this.chartOptions('column', 'AI Grayware', 'count')
    this.sandboxMalwareChartOptions = this.chartOptions('column', 'Sandbox Malware', 'count')
    this.sandboxCleanChartOptions = this.chartOptions('column', 'Sandbox Clean', 'count')

    this.urlsChartOptions = this.chartOptions('column', 'Urls', 'count')
    this.spamChartOptions = this.chartOptions('column', 'Spam', 'count')

    this.fetch()
  },

  computed: {
    dateRange: {
      get () {
        return this.$store.state.filterDateRange.dateRange
      },

      set (value) {
        this.$store.commit(mutationTypes.FILTER_DATE_RANGE, value)
      }
    },

    dateStart () {
      return moment(this.dateRange[0], C.M_FMT_MS).utc()
    },

    dateEnd () {
      return moment(this.dateRange[1], C.M_FMT_MS).utc()
    }
  },

  methods: {
    dateRangeFormat (date) {
      return moment(date).format(C.M_FMT_READABLE_WEEKDAY)
    },

    dateRangeChange (e) {
      this.dateRange = e
    },

    chartSeries (statName) {
      return statsLib.protocols.map((protocol) => {
        return {
          name: protocol,
          data: _.values(this.reducedProxyProtocolStats[protocol + '.' + statName])
        }
      })
    },

    chartOptions (type, title, yAxisTitle) {
        return {
          credits: {
            enabled: false
          },
          chart: {
            type: type
          },
          title: {
            text: title
          },
          xAxis: {
            categories: [],
            crosshair: true
          },
          yAxis: {
            min: 0,
            title: {
              text: yAxisTitle
            }
          },
          tooltip: {
            headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
            pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
            '<td style="padding:0"><b>{point.y}</b></td></tr>',
            footerFormat: '</table>',
            shared: true,
            useHTML: true
          },
          plotOptions: {
            column: {
              pointPadding: 0.2,
              borderWidth: 0
            }
          }
        }
    },

    reduceSoftwareVersions (softwareVersions) {
      var reducedVersions = _.chain(softwareVersions)
      .values()
      .flatten()
      .uniq()
      .map(function (version) {
        return version.split(':')[1]
      })
      .countBy()
      .value()

      // sort object by keys
      reducedVersions = _.object(_.sortBy(_.pairs(reducedVersions), function (o) { return o[0] }))

      return reducedVersions
    },

    reduceStats (stats) {
      var reducedStats = {}

      _.forEach(stats, (obj, key) => {
        reducedStats[key] = {}
        _.forEach(obj, (serialStats, date) => {
            reducedStats[key][date] = _.reduce(_.values(serialStats), (memo, count) => {
              return memo + parseInt(count)
            }, 0)
          })
        })

      return reducedStats
    },

    // Reduce the track log location data to a coordinate list suitable for mapping.
    // multiple appliances can share the same location, so reduce these to a single point,
    // with a link to each appliance in the popupText.
    reduceTrackCoordinates (trackIps) {
      const SEP = ':'

      var result = _.chain(trackIps)
      // the track log location information are store as object values
      .values()
      .flatten()
      .reject(_.isEmpty)
      // group by unique location LAT:LONG
      .groupBy(function (val) { return val.latitude + SEP + val.longitude })
      // map-reduce to consolidate the serials for each unique key together
      .map(function (value, key) {
        // console.log('MAPPING', key)
        var serials = _.reduce(value, function (memo, val) {
          memo[val.serial] = '<a href="/#/soc/serial/' + val.serial + '">' + val.serial.substring(0, 8) + '</a>'
          return memo
        }, {})
        return {latlong: key, serials: serials}
      })
      .value()

      // map the result to the coordinates format required by the map
      return result.map((item) => {
        var [ lat, long ] = item.latlong.split(SEP)

        return {
          lat: Number(lat),
          long: Number(long),
          popupText: _.values(item.serials).join('<br>')
        }
      })
    },

    fetch () {
      var appliance = this.$apiWsoc.fetchTotalAppliance(this.dateStart.format('x'), this.dateEnd.format('x'))
      var versions = this.$apiWsoc.fetchSoftwareVersions(this.dateStart.format('x'), this.dateEnd.format('x'))
      var trackIps = this.$apiWsoc.fetchTrackIp(this.dateStart.format('x'), this.dateEnd.format('x'))
      var proxyProtocolStats = this.$apiWsoc.fetchProxyProtocolStats(this.dateStart.format('x'), this.dateEnd.format('x'))

      this.$store.commit(mutationTypes.LOADER_LOADING)
      Promise.all([appliance, versions, trackIps, proxyProtocolStats])
      .then(([applianceResult, versionsResult, trackIpsResult, protocolStatsResult]) => {
        // console.log('fetched', trackIpsResult.result)

        this.totalAppliances = applianceResult.result
        this.softwareVersion = versionsResult.result
        this.proxyProtocolStats = protocolStatsResult.result
        this.trackCoordinates = this.reduceTrackCoordinates(trackIpsResult.result)

        // update graph options and series
        this.appliancesChartSeries = [{
          name: 'Appliances',
          data: _.values(this.totalAppliances)
        }]
        this.appliancesChartOptions.xAxis.categories = _.keys(this.totalAppliances)

        // reduce the software versions data
        this.reducedVersions = this.reduceSoftwareVersions(this.softwareVersion)
        this.versionsChartSeries = [{
          name: 'Versions',
          data: _.values(this.reducedVersions)
        }]
        this.versionsChartOptions.xAxis.categories = _.keys(this.reducedVersions)

        // reduce stats
        this.reducedProxyProtocolStats = this.reduceStats(this.proxyProtocolStats)

        // update chart options and series
        this.requestsChartSeries = this.chartSeries('totalRequests')
        this.requestsChartOptions.xAxis.categories = _.keys(this.reducedProxyProtocolStats['HTTP.totalRequests'])

        this.malwareChartSeries = this.chartSeries('blockedVirus')
        this.malwareChartOptions.xAxis.categories = _.keys(this.reducedProxyProtocolStats['HTTP.blockedVirus'])

        this.bytesChartSeries = this.chartSeries('totalBytesRead')
        this.bytesChartOptions.xAxis.categories = _.keys(this.reducedProxyProtocolStats['HTTP.totalBytesRead'])

        this.connectionsChartSeries = this.chartSeries('totalConnections')
        this.connectionsChartOptions.xAxis.categories = _.keys(this.reducedProxyProtocolStats['HTTP.totalConnections'])

        this.urlsChartSeries = [{
          name: 'Blocked URL',
          data: _.values(this.reducedProxyProtocolStats['HTTP.blockedUrls'])
        }, {
          name: 'Blocked WF URL',
          data: _.values(this.reducedProxyProtocolStats['HTTP.blockedWfUrls'])
        }, {
          name: 'Warned WF URL',
          data: _.values(this.reducedProxyProtocolStats['HTTP.warnedWfUrls'])
        }, {
          name: 'Blocked CM URL',
          data: _.values(this.reducedProxyProtocolStats['HTTP.blockedCMUrls'])
        }, {
          name: 'Warned CM URL',
          data: _.values(this.reducedProxyProtocolStats['HTTP.warnedCMUrls'])
        }]
        this.urlsChartOptions.xAxis.categories = _.keys(this.reducedProxyProtocolStats['HTTP.blockedUrls'])

        this.aiMalwareChartSeries = this.chartSeries('cylanceAvMalware')
        this.aiMalwareChartOptions.xAxis.categories = _.keys(this.reducedProxyProtocolStats['HTTP.cylanceAvMalware'])

        this.aiGraywareChartSeries = this.chartSeries('cylanceAvGrayware')
        this.aiGraywareChartOptions.xAxis.categories = _.keys(this.reducedProxyProtocolStats['HTTP.cylanceAvGrayware'])

        this.sandboxMalwareChartSeries = this.chartSeries('sandboxAvHashMalware')
        this.sandboxMalwareChartOptions.xAxis.categories = _.keys(this.reducedProxyProtocolStats['HTTP.sandboxAvHashMalware'])

        this.sandboxCleanChartSeries = this.chartSeries('sandboxAvHashClean')
        this.sandboxCleanChartOptions.xAxis.categories = _.keys(this.reducedProxyProtocolStats['HTTP.sandboxAvHashClean'])

        this.spamChartSeries = [{
          name: 'SMTP',
          data: _.values(this.reducedProxyProtocolStats['SMTP.spamCount'])
        }, {
          name: 'POP3',
          data: _.values(this.reducedProxyProtocolStats['POP3.spamCount'])
        }, {
          name: 'IMAP',
          data: _.values(this.reducedProxyProtocolStats['IMAP.spamCount'])
        }]
        this.spamChartOptions.xAxis.categories = _.keys(this.reducedProxyProtocolStats['SMTP.spamCount'])

        this.$store.commit(mutationTypes.LOADER_NOT_LOADING)
        // console.log('postfetch', this.appliancesChartOptions, this.appliancesChartSeries)
      })
      .catch((error) => {
        console.log('ERROR', error)
        this.$store.commit(mutationTypes.LOADER_NOT_LOADING)
      })
    }
  },

  watch: {
    dateRange: function () {
      this.fetch()
    }
  }
}
</script>



// WEBPACK FOOTER //
// Home.vue?96297f78